import { useEffect, useRef, useState } from "react";
import Axios from "axios";
import familySafeLogo from "../../assets/images/family-safe-logo.png";
import { API_ROUTE, Routes } from "../../helper/Routes";
import { activateMaintenanceBypass } from "../../helper/maintenanceBypass";
import { shouldShowPortalMaintenance } from "../../helper/portalMaintenance";
import "./AuthPageShell.css";

interface MaintenancePageProps {
  title?: string;
  message: string;
}

const MAINTENANCE_RECHECK_TIMEOUT_MS = 5000;
const MAINTENANCE_BYPASS_HOLD_MS = 5000;

const MaintenancePage = ({ title = "We’re working on FamilySafe", message }: MaintenancePageProps) => {
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [retryMessage, setRetryMessage] = useState<string | null>(null);
  const holdTimerRef = useRef<number | null>(null);

  const clearHoldTimer = () => {
    if (holdTimerRef.current !== null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearHoldTimer();
    };
  }, []);

  const handleLogoHoldStart = () => {
    clearHoldTimer();
    holdTimerRef.current = window.setTimeout(() => {
      activateMaintenanceBypass();
      window.location.replace("/login");
    }, MAINTENANCE_BYPASS_HOLD_MS);
  };

  const handleRetry = async () => {
    try {
      setCheckingAvailability(true);
      setRetryMessage(null);

      const response = await Axios.get<{
        resolutionStatus?: string;
        maintenance?: { isEnabled: boolean } | null;
      }>(
        `${API_ROUTE}${Routes.PORTAL_CONTEXT}`,
        { timeout: MAINTENANCE_RECHECK_TIMEOUT_MS }
      );

      if (!shouldShowPortalMaintenance(response.data)) {
        window.location.replace("/login");
        return;
      }

      setRetryMessage("FamilySafe is still unavailable. Please try again shortly.");
    } catch {
      setRetryMessage("FamilySafe is still unavailable. Please try again shortly.");
    } finally {
      setCheckingAvailability(false);
    }
  };

  return (
    <div className="auth-page-shell">
      <div className="auth-page-gradient" />
      <main className="auth-page-main">
        <section className="auth-page-card">
          <header className="auth-page-header">
            <button
              type="button"
              className="auth-page-logo-button"
              onPointerDown={handleLogoHoldStart}
              onPointerUp={clearHoldTimer}
              onPointerLeave={clearHoldTimer}
              onPointerCancel={clearHoldTimer}
            >
              <img className="auth-page-logo" src={familySafeLogo} alt="FamilySafe logo" />
            </button>
            <div className="auth-page-copy">
              <p className="auth-page-overline">Secure portal</p>
              <h1 className="auth-page-title-maintenance">{title}</h1>
              <p>The secure portal is temporarily unavailable.</p>
            </div>
          </header>

          <section className="auth-page-body">
            <div className="auth-maintenance-panel" role="status" aria-live="polite">
              <span className="auth-maintenance-pill">Service update</span>
              <p className="auth-maintenance-message">{message}</p>
              <p className="auth-maintenance-note">
                Please try again shortly.
              </p>
              <p className="auth-maintenance-note">
                If this page remains visible, refresh in a few minutes and try again.
              </p>
              <button
                className="auth-form-button"
                type="button"
                onClick={() => void handleRetry()}
                disabled={checkingAvailability}
              >
                {checkingAvailability ? "Checking availability..." : "Try again"}
              </button>
              {retryMessage ? (
                <p className="auth-maintenance-note" role="alert">
                  {retryMessage}
                </p>
              ) : null}
            </div>
          </section>

          <footer className="auth-page-footer">
            <p className="auth-form-legal">
              Your encrypted data remains protected. Sign-in is temporarily unavailable while the platform is updated.
            </p>
          </footer>
        </section>
      </main>
    </div>
  );
};

export default MaintenancePage;
